import { useState, useEffect } from 'react';
import { X, AlertTriangle, Copy } from 'lucide-react';
import { ExecutionHistoryDetail as ExecutionHistoryDetailType } from '../types/executionHistory';
import { ExecutionHistoryService } from '../services/executionHistoryService';
import { ExecutionStepsViewer } from './ExecutionStepsViewer';
import { EnhancedSearchResultsList } from './EnhancedSearchResultsList';
import { AnswerWithCitations } from './AnswerWithCitations';
import { StructuredAnswer } from './StructuredAnswer';
import { ExecutionScoreCard } from './ExecutionScoreCard';
import { AIExecutionScoreCard } from './AIExecutionScoreCard';
import { NoDataCard } from './NoDataCard';
import { CitationAnchor } from '../types/api';
import { extractConfigFromExecution } from '../utils/extractConfigFromExecution';

interface ExecutionHistoryDetailProps {
  executionId: string;
  onClose: () => void;
  onNotFound?: () => void;
  /** Optional validation reasoning from hallucination test scoring */
  hallucinationReasoning?: string;
  /** Optional hallucination test score */
  hallucinationScore?: number;
  /** Optional hallucination test name */
  hallucinationTestName?: string;
  /** Optional expected outcome for the hallucination test */
  hallucinationExpectation?: string;
}

export const ExecutionHistoryDetail = ({ executionId, onClose, onNotFound, hallucinationReasoning, hallucinationScore, hallucinationTestName, hallucinationExpectation }: ExecutionHistoryDetailProps) => {
  const [detail, setDetail] = useState<ExecutionHistoryDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evaluationExecutionId, setEvaluationExecutionId] = useState<string | null>(null);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [copyingConfig, setCopyingConfig] = useState(false);
  const [configCopyFeedback, setConfigCopyFeedback] = useState(false);
  const historyService = ExecutionHistoryService.getInstance();

  useEffect(() => {
    loadDetail();
  }, [executionId]);

  const loadDetail = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await historyService.getExecutionById(executionId);
      setDetail(data);

      // Check if this is an evaluation execution (format: baseId_providerId)
      if (executionId.includes('_')) {
        const lastUnderscoreIndex = executionId.lastIndexOf('_');
        const baseId = executionId.substring(0, lastUnderscoreIndex);
        const provId = executionId.substring(lastUnderscoreIndex + 1);
        setEvaluationExecutionId(baseId);
        setProviderId(provId);
      }
    } catch (err: any) {
      console.error('Failed to load execution detail:', err);
      const errorMessage = 'Failed to load execution details';
      setError(errorMessage);
      
      // If it's a 404 or similar "not found" error, trigger the fallback
      if (err.response?.status === 404 || err.message?.includes('not found')) {
        if (onNotFound) {
          // Delay slightly to show the error before redirecting
          setTimeout(() => {
            onNotFound();
          }, 1500);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyConfig = async () => {
    if (!detail?.flowData) return;
    setCopyingConfig(true);
    try {
      const json = await extractConfigFromExecution(detail.flowData, executionId);
      if (!json) {
        alert('Could not extract provider configuration from this execution (no agent steps found).');
        return;
      }
      await navigator.clipboard.writeText(json);
      setConfigCopyFeedback(true);
      setTimeout(() => setConfigCopyFeedback(false), 2000);
    } catch (err: any) {
      console.error('Failed to copy config:', err);
      alert(err.message || 'Failed to copy configuration');
    } finally {
      setCopyingConfig(false);
    }
  };



  return (
    <div data-component="ExecutionHistoryDetail" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Execution Details</h2>
          <div className="flex items-center gap-2">
            {detail?.flowData && (
              <button
                onClick={handleCopyConfig}
                disabled={copyingConfig}
                className="relative flex items-center gap-1 px-3 py-1.5 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Copy provider configuration to clipboard (ProviderSelector import format)"
              >
                <Copy className={`w-4 h-4 ${copyingConfig ? 'animate-spin' : ''}`} />
                {copyingConfig ? 'Copying...' : 'Copy Config'}
                {configCopyFeedback && (
                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                    Copied!
                  </span>
                )}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div data-flow="loading" className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading execution details...</div>
            </div>
          ) : error ? (
            <div data-flow="error" className="flex flex-col items-center justify-center h-64 text-red-600">
              <AlertTriangle className="w-16 h-16 mb-4" />
              <p>{error}</p>
            </div>
          ) : detail?.isLegacy ? (
            <div data-flow="legacy">
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Legacy Execution</span>
                </div>
                <p className="text-sm text-yellow-700 mt-1">
                  This execution uses an older schema version ({detail.schemaVersion}). Displaying raw data.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-gray-800 whitespace-pre-wrap">
                  {detail.rawJson}
                </pre>
              </div>
            </div>
          ) : detail?.flowData ? (
            <div data-flow="current-schema">
              <div className="space-y-6">
                {/* Basic Info Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Execution Summary</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Execution ID:</span>
                      <p className="font-mono text-xs mt-1">{detail.flowData.executionId}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">User ID:</span>
                      <p className="mt-1">{detail.flowData.userId || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">User Command:</span>
                      <p className="mt-1 col-span-2">{detail.flowData.userCommand}</p>
                    </div>
                  </div>
                </div>

                {/* Hallucination Test Validation Card */}
                {hallucinationReasoning && (
                  <div data-flow="hallucination-reasoning" className="bg-white rounded-lg border border-amber-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">Hallucination Test Validation</h3>
                      {hallucinationScore !== undefined && (
                        <span className={`text-sm font-semibold px-2 py-0.5 rounded ${
                          hallucinationScore >= 8 ? 'bg-green-100 text-green-700' :
                          hallucinationScore >= 5 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {hallucinationScore}/10
                        </span>
                      )}
                    </div>
                    {hallucinationTestName && (
                      <p className="text-xs text-gray-500 mb-3">Test: {hallucinationTestName}</p>
                    )}
                    {hallucinationExpectation && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Expectation</p>
                        <p className="text-sm text-gray-700 bg-amber-50 rounded px-3 py-2">{hallucinationExpectation}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Validation</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{hallucinationReasoning}</p>
                    </div>
                  </div>
                )}

                {/* Final Response Card - Only show for non-no-data responses */}
                {detail.flowData.finalResponse && 
                 !['no_data', 'no_relevant_data', 'no_relevant_results', 'needs_clarification'].includes(detail.flowData.finalResponse.status) && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Final Response</h3>
                    <div className="prose prose-sm max-w-none">
                      {detail.flowData.finalResponse.status && (
                        <div className="mb-2">
                          <span className="text-xs font-medium text-gray-500">Status: </span>
                          <span className="text-xs font-semibold text-blue-600">{detail.flowData.finalResponse.status}</span>
                        </div>
                      )}
                      {detail.flowData.finalResponse.message && (
                        <p className="text-sm text-gray-600 mb-2">{detail.flowData.finalResponse.message}</p>
                      )}
                      {detail.flowData.finalResponse.answer && (
                        <div data-flow="answer-with-citations">
                          {detail.flowData.finalResponse.paragraphs && detail.flowData.finalResponse.paragraphs.length > 0 ? (
                            <StructuredAnswer
                              paragraphs={detail.flowData.finalResponse.paragraphs}
                              opinions={detail.flowData.searchedOpinions}
                            />
                          ) : detail.flowData.finalResponse.citations && detail.flowData.finalResponse.citations.length > 0 ? (
                            <AnswerWithCitations 
                              answer={detail.flowData.finalResponse.answer}
                              citations={detail.flowData.finalResponse.citations as CitationAnchor[]}
                              opinions={detail.flowData.searchedOpinions}
                            />
                          ) : (
                            <p className="text-gray-700 whitespace-pre-wrap">{detail.flowData.finalResponse.answer}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Display sources for research workflow */}
                      {detail.flowData.finalResponse.sources && detail.flowData.finalResponse.sources.length > 0 && (
                        <div className="mt-4" data-flow="sources">
                          <p className="text-sm font-medium text-gray-600 mb-2">Sources ({detail.flowData.finalResponse.sources.length}):</p>
                          <ul className="text-sm space-y-1">
                            {detail.flowData.finalResponse.sources.map((source: any, idx: number) => (
                              <li key={idx} className="text-gray-600">
                                {source.title || source.opinionId}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Needs Clarification Card - Show orchestrator questions */}
                {detail.flowData.finalResponse?.status === 'needs_clarification' && (
                  <div data-flow="needs-clarification" className="bg-white rounded-lg border border-orange-200 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      <h3 className="font-semibold text-orange-800">Clarification Needed</h3>
                    </div>
                    {detail.flowData.finalResponse.message && (
                      <p className="text-sm text-gray-700 mb-3">{detail.flowData.finalResponse.message}</p>
                    )}
                    {detail.flowData.orchestratorDecision?.questions?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-600 mb-2">Questions:</p>
                        <ul className="list-disc list-inside space-y-1">
                          {detail.flowData.orchestratorDecision.questions.map((q: string, idx: number) => (
                            <li key={idx} className="text-sm text-gray-700">{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* No Data Card - Show when final response has a no-data status */}
                {detail.flowData.finalResponse && 
                 ['no_data', 'no_relevant_data', 'no_relevant_results'].includes(detail.flowData.finalResponse.status) && (
                  <div data-flow="no-data" className="bg-white rounded-lg border border-gray-200 p-4">
                    <NoDataCard
                      status={detail.flowData.finalResponse.status as 'no_data' | 'no_relevant_data' | 'no_relevant_results'}
                      message={detail.flowData.finalResponse.message}
                      answer={detail.flowData.finalResponse.answer}
                    />
                  </div>
                )}

                {/* Execution Score Card - Only show for completed executions */}
                {detail.flowData.status?.toLowerCase() === 'completed' && detail.flowData.executionId && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900 mb-4">User Ratings</h3>
                    <ExecutionScoreCard executionId={detail.flowData.executionId} />
                  </div>
                )}

                {/* AI Execution Score Card - Only show for evaluation executions */}
                {detail.flowData.status?.toLowerCase() === 'completed' && evaluationExecutionId && providerId && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900 mb-4">AI Quality Score</h3>
                    <AIExecutionScoreCard 
                      evaluationExecutionId={evaluationExecutionId}
                      providerId={providerId}
                    />
                  </div>
                )}

                {/* Search Results - Using EnhancedSearchResultsList */}
                {detail.flowData.finalResponse?.searchResults && (
                  <EnhancedSearchResultsList 
                    searchResults={detail.flowData.finalResponse.searchResults}
                  />
                )}

                {/* Detailed Execution Flow - Using ExecutionStepsViewer */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-4">Detailed Execution Flow</h3>
                  <ExecutionStepsViewer executionFlow={detail.flowData} />
                </div>
              </div>
            </div>
          ) : (
            <div data-flow="no-data" className="flex items-center justify-center h-64 text-gray-500">
              <p>No execution data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
